/**
 * Scales tuned in just intonation relative to Sa (the tonic drone).
 * `semitones` are the 12-TET degrees from Sa (used for labels, fire-ring shading, and the 12-TET toggle);
 * `ratios` are the JI frequency ratios to Sa for the same degrees.
 * `anchor` is the tanpura's second string: which degree gets the widened west gate and the auto-fifth layer.
 */
export type Anchor = 'Pa' | 'Ma' | 'Ni';

export interface Scale {
  id: string;
  /** Display name: "Raga (Mode)" */
  name: string;
  raga: string;
  mode: string;
  semitones: number[];
  ratios: number[];
  anchor: Anchor;
  /** Degrees (as indexes into semitones) that define the scale's character; the consonance guard never nudges these. */
  defining: number[];
  extra?: boolean;
  character: string;
}

const S = (id: string, raga: string, mode: string, semitones: number[], ratios: number[], anchor: Anchor, defining: number[], character: string, extra = false): Scale => ({
  id, name: `${raga} (${mode})`, raga, mode, semitones, ratios, anchor, defining, character, extra,
});

export const SCALES: Scale[] = [
  S('yaman', 'Yaman', 'Lydian', [0, 2, 4, 6, 7, 9, 11], [1, 9 / 8, 5 / 4, 45 / 32, 3 / 2, 5 / 3, 15 / 8], 'Pa', [3], 'auspicious, floating, cosmic'),
  S('bhupali', 'Bhupali', 'Major pentatonic', [0, 2, 4, 7, 9], [1, 9 / 8, 5 / 4, 3 / 2, 5 / 3], 'Pa', [], 'serene; no wrong notes at all'),
  S('khamaj', 'Khamaj', 'Mixolydian', [0, 2, 4, 5, 7, 9, 10], [1, 9 / 8, 5 / 4, 4 / 3, 3 / 2, 5 / 3, 7 / 4], 'Pa', [6], 'pastoral; the bagpipe mode'),
  S('kafi', 'Kafi', 'Dorian', [0, 2, 3, 5, 7, 9, 10], [1, 9 / 8, 6 / 5, 4 / 3, 3 / 2, 5 / 3, 9 / 5], 'Pa', [], 'longing, night'),
  S('darbari', 'Darbari', 'Aeolian', [0, 2, 3, 5, 7, 8, 10], [1, 9 / 8, 6 / 5, 4 / 3, 3 / 2, 8 / 5, 9 / 5], 'Pa', [], 'grave, midnight'),
  S('bhairav', 'Bhairav', 'Double harmonic', [0, 1, 4, 5, 7, 8, 11], [1, 16 / 15, 5 / 4, 4 / 3, 3 / 2, 8 / 5, 15 / 8], 'Pa', [1, 5], 'solemn sunrise'),
  S('malkauns', 'Malkauns', 'Minor pentatonic ♭6', [0, 3, 5, 8, 10], [1, 6 / 5, 4 / 3, 8 / 5, 16 / 9], 'Ma', [], 'deep, late night'),
  S('marwa', 'Marwa', 'Lydian ♭2, no fifth', [0, 1, 4, 6, 9, 11], [1, 16 / 15, 5 / 4, 45 / 32, 5 / 3, 15 / 8], 'Ni', [1, 3], 'sunset unease'),
  S('in', 'In', 'Sakura', [0, 1, 5, 7, 8], [1, 16 / 15, 4 / 3, 3 / 2, 8 / 5], 'Pa', [1, 4], 'spare, Japanese'),
  S('overtone', 'Overtone', 'Acoustic', [0, 2, 4, 6, 7, 9, 10], [1, 9 / 8, 5 / 4, 11 / 8, 3 / 2, 13 / 8, 7 / 4], 'Pa', [3, 5, 6], 'harmonics 8–14; the most physical'),
  S('bhimpalasi', 'Bhimpalasi', 'Dorian pentatonic', [0, 3, 5, 7, 10], [1, 6 / 5, 4 / 3, 3 / 2, 9 / 5], 'Pa', [], 'early afternoon', true),
  S('bhairavi', 'Bhairavi', 'Phrygian', [0, 1, 3, 5, 7, 8, 10], [1, 16 / 15, 6 / 5, 4 / 3, 3 / 2, 8 / 5, 9 / 5], 'Pa', [1], 'dawn, tender', true),
  S('todi', 'Todi', 'Todi', [0, 1, 3, 6, 7, 8, 11], [1, 16 / 15, 6 / 5, 45 / 32, 3 / 2, 8 / 5, 15 / 8], 'Pa', [1, 3], 'pensive late morning', true),
  S('harmonicminor', 'Harmonic minor', 'Harmonic minor', [0, 2, 3, 5, 7, 8, 11], [1, 9 / 8, 6 / 5, 4 / 3, 3 / 2, 8 / 5, 15 / 8], 'Pa', [6], 'dramatic', true),
];

export const DEFAULT_SCALE_ID = 'yaman';
export const scaleById = (id: string): Scale => SCALES.find((s) => s.id === id) ?? SCALES[0]!;

export const PITCH_CLASS_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
/** Fire ring order: circle of fifths, index 0 = C at east, ascending counter-clockwise. */
export const FIFTHS_ORDER: number[] = Array.from({ length: 12 }, (_, i) => (i * 7) % 12);

/** Sargam name for a degree, given its semitone offset from Sa. Komal (flat) degrees lowercase, tivra Ma marked. */
export function sargamName(semitone: number): string {
  const names: Record<number, string> = { 0: 'Sa', 1: 're', 2: 'Re', 3: 'ga', 4: 'Ga', 5: 'Ma', 6: 'Ma°', 7: 'Pa', 8: 'dha', 9: 'Dha', 10: 'ni', 11: 'Ni', 12: "Sa'" };
  return names[semitone] ?? '?';
}
